import {produce} from 'immer';
import React from 'react';
import {assign, setup, fromPromise, assertEvent} from 'xstate';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {TranslationParameters} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import * as AntibodyNestedDataCreationSpreadsheetImportDAO from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportDAO';
import {QueryAntibodyComponentAndRelatedSchemasInput} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportDAO';
import {
  columnsIncludeDnaType,
  getColumnInferenceErrorMessages,
  getResolvedColumnType,
} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {
  AntibodyNestedDataCreationSpreadsheetImportState,
  AntibodyComponentAndRelatedSchemas,
  INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE,
  INITIAL_GERMLINE_CONFIG,
  NdcColumnState,
  NdcSpreadsheetInferredColumnErrorResponse,
  NdcStartingType,
  NdcGermlineConfig,
  NdcRelatedSchemasConfig,
  isNdcColumnInferenceResult,
  isNdcColumnInferenceResponse,
  NdcColumnInferenceRequest,
  NdcColumnInferenceResponse,
  NdcSpreadsheetInferredColumnResponse,
  NdcSpreadsheetImportColumnType,
  NdcSpreadsheetImportRequest,
  NdcSpreadsheetImportValidationErrorBadRequest,
  NdcSpreadsheetInterpretation,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {assert, assertNotNullAndReturn} from 'Asserts';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import notifications from 'notifications';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as logging from 'util/Logging';
import {getUserMessage, isServerRequestError} from 'util/Net';

async function handleInferColumnsForNestedCreateAndRegisterSpreadsheet(
  input: NdcColumnInferenceRequest
): Promise<NdcColumnInferenceResponse | {errorMessage: string}> {
  try {
    const result =
      await AntibodyNestedDataCreationSpreadsheetImportDAO.inferColumnsForNestedCreateAndRegisterSpreadsheet(
        input
      );
    return result;
  } catch (e) {
    if (isServerRequestError(e)) {
      const userMessage = getUserMessage(e);
      const errorMessage = userMessage ?? 'Failed to infer column types for spreadsheet import';
      return {errorMessage};
    } else {
      logging.error(e, {extra: {message: 'Failed to infer column types for spreadsheet import'}});
      notifications.notifyError(e);
      throw e;
    }
  }
}

async function handleNestedCreateAndRegisterFromSpreadsheetImport(
  input: NdcSpreadsheetImportRequest
): Promise<void | NdcSpreadsheetImportValidationErrorBadRequest> {
  try {
    LoadingScreen.show('Creating proteins from spreadsheet...');
    const result =
      await AntibodyNestedDataCreationSpreadsheetImportDAO.nestedCreateAndRegisterFromSpreadsheetImport(
        input
      );
    if (result.ndcWorkspaceUrl) {
      notifications.info(
        <>
          <p>We're working on creating proteins and components...</p>
          <a href={result.ndcWorkspaceUrl}>View progress</a>
        </>
      );
    } else {
      notifications.info("We're working on creating proteins and components...");
    }
  } catch (e) {
    // Catch BadRequestError and get the validationErrors from it, if they're set
    if (e.status === 400 && e.validationErrors != null) {
      const validationErrors = e.validationErrors;
      return {validationErrors};
    } else {
      logging.error(e, {
        extra: {message: 'Failed to create and register proteins from spreadsheet import'},
      });
      notifications.notifyError(e);
      throw e;
    }
  } finally {
    LoadingScreen.hide();
  }
}

function guardAntibodySchemaAndFormatAndRegistryAreSet(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): boolean {
  return (
    context.selectedFormatId != null && context.selectedSchemaId != null && context.selectedRegistryId != null
  );
}

/**
 * Apply downstream effects to context when the format changes.
 *
 * Automatically changing starting type from PAIRS to DOMAINS  if the selected format has no variable pairs.
 */
function applyFormatSideEffects(
  draft: AntibodyNestedDataCreationSpreadsheetImportState,
  format: AntibodyFormat | null
): void {
  if (
    format != null &&
    format.distinctVariablePairs.length === 0 &&
    draft.selectedStartingType === NdcStartingType.PAIRS
  ) {
    draft.selectedStartingType = NdcStartingType.DOMAINS;
  }
}

function guardSpreadsheetSetAndInterpretedWithoutError(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): boolean {
  return (
    context.spreadsheetFile != null &&
    context.spreadsheetURL != null &&
    context.spreadsheetInterpretation != null &&
    context.columnInferenceErrorMessages.length === 0
  );
}

function guardSpreadsheetSetAndNotInferred(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): boolean {
  return (
    context.spreadsheetFile != null &&
    context.spreadsheetURL != null &&
    context.spreadsheetInterpretation == null &&
    context.columnInferenceErrorMessages.length === 0
  );
}

/** Guards that column inference returned a valid response where all columns have non-empty inferred types.
 * Returns false for spreadsheet-wide errors, per-column errors, or columns with no inferred types. */
function guardColumnInferenceSucceededAndIsValid(
  output: NdcColumnInferenceResponse | {errorMessage: string}
): boolean {
  return (
    isNdcColumnInferenceResponse(output) &&
    output.columnTypes.every((columnType) => {
      if (isNdcColumnInferenceResult(columnType) && columnType.inferredComponentTypes.length > 0) {
        return true;
      }
      if (isNdcColumnInferenceResult(columnType) && columnType.inferredComponentTypes.length === 0) {
        logging.error('Received column type with no inferred types or error message');
      }
      return false;
    })
  );
}

function parseColumnInferenceResponseToSpreadsheetInterpretation(
  columnInferenceResponse: NdcColumnInferenceResponse
): NdcSpreadsheetInterpretation {
  const columns = columnInferenceResponse.columnTypes.map(
    (column: NdcSpreadsheetInferredColumnResponse | NdcSpreadsheetInferredColumnErrorResponse) => {
      assert(isNdcColumnInferenceResult(column), 'Expected all columns to be valid inferred columns');
      return {
        columnName: column.columnName,
        sampleValue: column.sampleValue,
        componentFormatKey: column.componentFormatKey,
        // Asserted to not be empty in guardColumnInferenceSucceededAndIsValid
        inferredTypes: column.inferredComponentTypes,
        // We automatically set the selected type to the only inferred type if there is only
        // one, so that the ColumnMappingPane knows not to display a warning to the user for
        // this column.
        selectedType: column.inferredComponentTypes.length === 1 ? column.inferredComponentTypes[0] : null,
      };
    }
  );

  return {
    columns,
    rowCount: columnInferenceResponse.rowCount,
  };
}

function guardSpreadsheetInterpretationIsValid(
  spreadsheetInterpretation: NdcSpreadsheetInterpretation | null
): boolean {
  return (
    spreadsheetInterpretation != null &&
    spreadsheetInterpretation.columns.every((column) => column.inferredTypes.length > 0)
  );
}

/** Validates that all related schemas are available, returning an error message if any of the user-selectable
 * related schemas are missing. DNA schemas are only validated when the columns include DNA types. */
function validateAvailableSchemas(
  schemas: AntibodyComponentAndRelatedSchemas,
  columns: Array<NdcColumnState>
): string | null {
  const missingSchemas = [];
  if (columnsIncludeDnaType(columns)) {
    if (schemas.dnaDomainSchemaIds.size === 0) {
      missingSchemas.push('DNA domain');
    }
    if (schemas.dnaChainSchemaIds.size === 0) {
      missingSchemas.push('DNA chain');
    }
    if (schemas.dnaPairSchemaIds.size === 0) {
      missingSchemas.push('DNA pair');
    }
  }
  if (schemas.aaPairSchemaIds.size === 0) {
    missingSchemas.push('AA pair');
  }
  return missingSchemas.length > 0 ? `No ${missingSchemas.join(', ')} schemas found in registry` : null;
}

function guardRelatedSchemasConfigIsValid(
  relatedSchemasConfig: NdcRelatedSchemasConfig,
  columns: Array<NdcColumnState>
): boolean {
  if (columnsIncludeDnaType(columns)) {
    if (
      relatedSchemasConfig.dnaDomainSchemaId == null ||
      relatedSchemasConfig.dnaChainSchemaId == null ||
      relatedSchemasConfig.dnaPairSchemaId == null
    ) {
      return false;
    }
  }
  return relatedSchemasConfig.aaPairSchemaId != null;
}

function guardSelectedProjectFolderIsValid(selectedProjectFolderId: string | null): boolean {
  return selectedProjectFolderId != null;
}

// TODO(BNCH-147016): Include germlineConfig in the submit payload and add a guard
// to validate that required germlines are selected before submitting.

/** Prepares the NdcSpreadsheetImportRequest for submitting the spreadsheet. Will assert on all required
 * fields being ready, assuming that all of these have been guarded against in earlier state transitions. */
function prepareNdcSpreadsheetImportRequest(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): NdcSpreadsheetImportRequest {
  assert(context.selectedProjectFolderId != null, 'Project folder ID should be set');
  assert(context.selectedSchemaId != null, 'Schema ID should be set');
  assert(context.spreadsheetURL != null, 'Spreadsheet URL should be set');
  assert(context.spreadsheetInterpretation != null, 'Spreadsheet interpretation should be set');
  assert(
    guardRelatedSchemasConfigIsValid(context.relatedSchemasConfig, context.spreadsheetInterpretation.columns),
    'Related schemas config should be valid'
  );

  const columns: Array<NdcSpreadsheetImportColumnType> = context.spreadsheetInterpretation.columns.map(
    (column) => {
      const resolvedType = getResolvedColumnType(column);

      return {
        columnName: column.columnName,
        componentFormatKey: column.componentFormatKey,
        ...resolvedType,
      };
    }
  );

  return {
    folderId: context.selectedProjectFolderId,
    antibodySchemaId: context.selectedSchemaId,
    spreadsheetLocation: context.spreadsheetURL,
    columns,
    translationParameters: context.translationParameters,
    documentId: context.selectedDocumentId,
    ...context.relatedSchemasConfig,
  };
}

type AntibodyNestedDataCreationSpreadsheetImportModalEvents =
  // Navigation
  | {type: 'next'}
  | {type: 'back'}
  // Select Format pane
  | {type: 'selectFormat'; format: AntibodyFormat | null}
  | {
      type: 'selectSchema';
      schemaId: string | null;
      schemaName: string | null;
      registryId: string | null;
      format: AntibodyFormat | null;
    }
  | {type: 'setStartingType'; startingType: NdcStartingType}
  | {type: 'setGermlineConfig'; germlineConfig: NdcGermlineConfig}
  // Upload Spreadsheet pane
  | {type: 'setSpreadsheetFile'; spreadsheetFile: File}
  | {type: 'setSpreadsheetURL'; spreadsheetURL: string}
  | {type: 'clearSpreadsheet'}
  // Column Mapping pane
  | {type: 'updateSpreadsheetInterpretation'; spreadsheetInterpretation: NdcSpreadsheetInterpretation}
  // Finalize pane
  | {type: 'setTranslationParameters'; translationParameters: TranslationParameters}
  | {type: 'setProjectFolder'; projectFolderId: string}
  | {type: 'setRelatedSchemasConfig'; relatedSchemasConfig: NdcRelatedSchemasConfig}
  | {type: 'setSelectedDocumentId'; documentId: string | null}
  | {type: 'submitSpreadsheetImport'}
  // Validation Errors pane
  | {type: 'backToUploadSpreadsheet'};

/**
 * State machine handling the progress and state of importing antibodies from a spreadsheet in a modal.
 *
 * -----------------------------------------------------------------------------------------------------------
 * | State                | Purpose                                                                          |
 * |----------------------|----------------------------------------------------------------------------------|
 * | selectFormat         | User picks antibody format, schema, registry, starting type (domains/chains/pairs)|
 * | uploadSpreadsheet    | User uploads a spreadsheet file (stored locally + S3 URL), backend infers column |
 * |                      | types from headers and first row and displays errors requiring reupload          |
 * | columnMapping        | User reviews/picks inferred column types for each spreadsheet column when        |
 * |                      | multiple inferred types are present                                              |
 * | finalize             | User picks project folder and related schemas                                    |
 * | saving               | Loading state while backend validates and kicks off the pipeline                 |
 * | validationErrors     | Shows validation errors thrown during validation (e.g. missing entities)         |
 * | done                 | Modal closes since pipeline was successfully kicked off                          |
 * ---------------------------------------------------------------------------------------------------------*/
const AntibodyNestedDataCreationSpreadsheetImportModalMachine = setup({
  types: {
    context: {} as AntibodyNestedDataCreationSpreadsheetImportState,
    events: {} as AntibodyNestedDataCreationSpreadsheetImportModalEvents,
    input: {} as {initialProjectFolderId?: string | null},
  },
  actors: {
    inferColumnTypes: fromPromise(
      async ({
        input,
      }: {
        input: NdcColumnInferenceRequest;
      }): Promise<NdcColumnInferenceResponse | {errorMessage: string}> =>
        await handleInferColumnsForNestedCreateAndRegisterSpreadsheet(input)
    ),
    submitSpreadsheetImport: fromPromise(
      async ({input}: {input: NdcSpreadsheetImportRequest}) =>
        await handleNestedCreateAndRegisterFromSpreadsheetImport(input)
    ),
    queryComponentAndRelatedSchemas: fromPromise(
      async ({
        input,
      }: {
        input: QueryAntibodyComponentAndRelatedSchemasInput;
      }): Promise<AntibodyComponentAndRelatedSchemas> =>
        await AntibodyNestedDataCreationSpreadsheetImportDAO.queryAntibodyComponentAndRelatedSchemas(input)
    ),
  },
  actions: {
    // Select Format pane
    selectFormat: assign(({context, event}) => {
      assertEvent(event, 'selectFormat');
      const newFormatId = event.format?.id ?? null;

      return produce(context, (draft) => {
        draft.selectedFormatId = newFormatId;

        // Clear schema, registry, and germlines if format changed
        if (context.selectedFormatId !== newFormatId) {
          draft.selectedSchemaId = null;
          draft.selectedSchemaName = null;
          draft.selectedRegistryId = null;
          draft.germlineConfig = INITIAL_GERMLINE_CONFIG;
        }
        applyFormatSideEffects(draft, event.format);
      });
    }),
    selectSchema: assign(({context, event}) => {
      assertEvent(event, 'selectSchema');
      if (context.selectedSchemaId === event.schemaId) {
        return context;
      }
      // Reset downstream state that depends on the schema. Spreadsheet file/URL are preserved so
      // the user doesn't have to re-upload; re-inference triggers automatically on navigation.
      const baseState = {
        ...INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE,
        selectedFormatId: event.format?.id ?? null,
        selectedRegistryId: event.registryId,
        selectedStartingType: context.selectedStartingType,
        selectedProjectFolderId: context.selectedProjectFolderId,
        translationParameters: context.translationParameters,
        spreadsheetFile: context.spreadsheetFile,
        spreadsheetURL: context.spreadsheetURL,
        selectedSchemaId: event.schemaId,
        selectedSchemaName: event.schemaName,
        germlineConfig: context.germlineConfig,
      };

      return produce(baseState, (draft) => {
        applyFormatSideEffects(draft, event.format);
      });
    }),
    setStartingType: assign(({context, event}) => {
      assertEvent(event, 'setStartingType');
      if (context.selectedStartingType === event.startingType) {
        return context;
      }
      // Reset downstream state that depends on starting type. Spreadsheet file/URL are preserved so
      // the user doesn't have to re-upload; re-inference triggers automatically on navigation.
      return {
        ...INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE,
        selectedFormatId: context.selectedFormatId,
        selectedSchemaId: context.selectedSchemaId,
        selectedSchemaName: context.selectedSchemaName,
        selectedRegistryId: context.selectedRegistryId,
        selectedProjectFolderId: context.selectedProjectFolderId,
        translationParameters: context.translationParameters,
        spreadsheetFile: context.spreadsheetFile,
        spreadsheetURL: context.spreadsheetURL,
        selectedStartingType: event.startingType,
        germlineConfig: context.germlineConfig,
      };
    }),
    setGermlineConfig: assign(({context, event}) => {
      assertEvent(event, 'setGermlineConfig');
      return produce(context, (draft) => {
        draft.germlineConfig = event.germlineConfig;
      });
    }),
    // Upload Spreadsheet pane
    setSpreadsheetFile: assign(({context, event}) => {
      assertEvent(event, 'setSpreadsheetFile');
      return produce(context, (draft) => {
        draft.spreadsheetFile = event.spreadsheetFile;
        draft.spreadsheetInterpretation = null;
        draft.columnInferenceErrorMessages = [];
      });
    }),
    setSpreadsheetURL: assign(({context, event}) => {
      assertEvent(event, 'setSpreadsheetURL');
      return produce(context, (draft) => {
        draft.spreadsheetURL = event.spreadsheetURL;
        draft.spreadsheetInterpretation = null;
        draft.columnInferenceErrorMessages = [];
      });
    }),
    clearSpreadsheet: assign(({context, event}) => {
      assertEvent(event, 'clearSpreadsheet');
      return produce(context, (draft) => {
        draft.spreadsheetFile = null;
        draft.spreadsheetURL = null;
        draft.spreadsheetInterpretation = null;
        draft.columnInferenceErrorMessages = [];
      });
    }),
    // Column Mapping pane
    updateSpreadsheetInterpretation: assign(({context, event}) => {
      assertEvent(event, 'updateSpreadsheetInterpretation');
      return produce(context, (draft) => {
        draft.spreadsheetInterpretation = event.spreadsheetInterpretation;
      });
    }),
    // Finalize pane
    setTranslationParameters: assign(({context, event}) => {
      assertEvent(event, 'setTranslationParameters');
      return produce(context, (draft) => {
        draft.translationParameters = event.translationParameters;
      });
    }),
    setProjectFolder: assign(({context, event}) => {
      assertEvent(event, 'setProjectFolder');
      return produce(context, (draft) => {
        draft.selectedProjectFolderId = event.projectFolderId;
      });
    }),
    setRelatedSchemasConfig: assign(({context, event}) => {
      assertEvent(event, 'setRelatedSchemasConfig');
      return produce(context, (draft) => {
        draft.relatedSchemasConfig = event.relatedSchemasConfig;
      });
    }),
    resetStateForSpreadsheetReupload: assign(({context, event}) => {
      assertEvent(event, 'backToUploadSpreadsheet');
      return {
        ...INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE,
        selectedFormatId: context.selectedFormatId,
        selectedSchemaId: context.selectedSchemaId,
        selectedSchemaName: context.selectedSchemaName,
        selectedRegistryId: context.selectedRegistryId,
        selectedStartingType: context.selectedStartingType,
        selectedProjectFolderId: context.selectedProjectFolderId,
        translationParameters: context.translationParameters,
        germlineConfig: context.germlineConfig,
      };
    }),
    setSelectedDocumentId: assign(({context, event}) => {
      assertEvent(event, 'setSelectedDocumentId');
      return produce(context, (draft) => {
        draft.selectedDocumentId = event.documentId;
      });
    }),
  },
}).createMachine({
  context: ({input}) =>
    produce(INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE, (draft) => {
      draft.selectedProjectFolderId =
        input.initialProjectFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY) ?? null;
    }),
  id: 'antibodyNestedDataCreationSpreadsheetImportModal',
  initial: 'selectFormat',
  states: {
    selectFormat: {
      on: {
        next: {
          target: 'uploadSpreadsheet',
          // TODO(BNCH-147016): Add a guard to validate that required germlines are selected
          // (using formatHasBothDomainTypes to determine which germlines are needed).
          guard: ({context}) => guardAntibodySchemaAndFormatAndRegistryAreSet(context),
        },
        selectFormat: {actions: 'selectFormat'},
        selectSchema: {actions: 'selectSchema'},
        setStartingType: {actions: 'setStartingType'},
        setGermlineConfig: {actions: 'setGermlineConfig'},
      },
    },
    uploadSpreadsheet: {
      initial: 'selectingSpreadsheet',
      on: {
        next: {
          target: 'columnMapping',
          guard: ({context}) => guardSpreadsheetSetAndInterpretedWithoutError(context),
        },
        back: {
          target: 'selectFormat',
        },
      },
      states: {
        selectingSpreadsheet: {
          always: {
            target: 'inferringColumns',
            guard: ({context}) => guardSpreadsheetSetAndNotInferred(context),
          },
          on: {
            setSpreadsheetFile: {actions: 'setSpreadsheetFile'},
            setSpreadsheetURL: {actions: 'setSpreadsheetURL'},
            clearSpreadsheet: {actions: 'clearSpreadsheet'},
          },
        },
        inferringColumns: {
          invoke: {
            src: 'inferColumnTypes',
            input: ({
              context,
            }: {
              context: AntibodyNestedDataCreationSpreadsheetImportState;
            }): NdcColumnInferenceRequest => {
              assert(context.spreadsheetURL != null, 'Spreadsheet URL should be set');
              assert(context.selectedSchemaId != null, 'Schema ID should be set');
              return {
                spreadsheetLocation: context.spreadsheetURL,
                antibodySchemaId: context.selectedSchemaId,
                componentFormatType: context.selectedStartingType,
              };
            },
            onDone: [
              {
                target: 'columnsInferred',
                guard: ({event}) => guardColumnInferenceSucceededAndIsValid(event.output),
                actions: assign(({context, event}) => {
                  const columnInferenceResponse = event.output;
                  assert(
                    isNdcColumnInferenceResponse(columnInferenceResponse),
                    'Action guard should prevent action from running if column inference returned an error'
                  );
                  return produce(context, (draft) => {
                    draft.spreadsheetInterpretation =
                      parseColumnInferenceResponseToSpreadsheetInterpretation(columnInferenceResponse);
                  });
                }),
              },
              {
                target: 'showingError',
                guard: ({event}) => !guardColumnInferenceSucceededAndIsValid(event.output),
                actions: assign(({context, event}) => {
                  const errorMessages = getColumnInferenceErrorMessages(event.output);
                  assert(
                    errorMessages.length > 0,
                    'Column inference guard rejected the response but no error messages were extracted'
                  );
                  return produce(context, (draft) => {
                    draft.columnInferenceErrorMessages = errorMessages;
                  });
                }),
              },
            ],
            onError: {
              target: 'showingError',
              actions: assign(({context}) =>
                produce(context, (draft) => {
                  draft.columnInferenceErrorMessages = [
                    'An unexpected error occurred while processing your spreadsheet.',
                  ];
                })
              ),
            },
          },
          on: {
            clearSpreadsheet: {
              target: 'selectingSpreadsheet',
              actions: 'clearSpreadsheet',
            },
          },
        },
        columnsInferred: {
          on: {
            clearSpreadsheet: {
              target: 'selectingSpreadsheet',
              actions: 'clearSpreadsheet',
            },
          },
        },
        showingError: {
          on: {
            clearSpreadsheet: {
              target: 'selectingSpreadsheet',
              actions: 'clearSpreadsheet',
            },
          },
        },
      },
    },
    columnMapping: {
      on: {
        next: {
          target: 'finalize',
          guard: ({context}) => guardSpreadsheetInterpretationIsValid(context.spreadsheetInterpretation),
        },
        back: {
          target: 'uploadSpreadsheet',
        },
        updateSpreadsheetInterpretation: {
          actions: 'updateSpreadsheetInterpretation',
        },
      },
    },
    finalize: {
      invoke: {
        src: 'queryComponentAndRelatedSchemas',
        input: ({
          context,
        }: {
          context: AntibodyNestedDataCreationSpreadsheetImportState;
        }): QueryAntibodyComponentAndRelatedSchemasInput => {
          assert(context.selectedRegistryId != null, 'Registry ID should be set');
          assert(context.selectedSchemaId != null, 'Schema ID should be set');
          return {
            registryId: context.selectedRegistryId,
            antibodySchemaId: context.selectedSchemaId,
          };
        },
        onDone: {
          actions: assign(({context, event}) => {
            // spreadsheetInterpretation is guarded against being null in guardSpreadsheetInterpretationIsValid
            const componentAndRelatedSchemasErrorMessage = validateAvailableSchemas(
              event.output,
              assertNotNullAndReturn(context.spreadsheetInterpretation).columns
            );
            return produce(context, (draft) => {
              draft.componentAndRelatedSchemasErrorMessage = componentAndRelatedSchemasErrorMessage;
              draft.componentAndRelatedSchemas =
                componentAndRelatedSchemasErrorMessage == null ? event.output : null;
            });
          }),
        },
        onError: {
          actions: assign(({context, event}) => {
            logging.error(event.error as Error, {
              extra: {message: 'Failed to fetch component and related schemas'},
            });

            return produce(context, (draft) => {
              draft.componentAndRelatedSchemasErrorMessage =
                'There was an error fetching schemas from the registry.';
            });
          }),
        },
      },
      on: {
        back: {
          target: 'columnMapping',
        },
        submitSpreadsheetImport: {
          target: 'saving',
          guard: ({context}) =>
            guardRelatedSchemasConfigIsValid(
              context.relatedSchemasConfig,
              // spreadsheetInterpretation is guarded against being null in guardSpreadsheetInterpretationIsValid
              assertNotNullAndReturn(context.spreadsheetInterpretation).columns
            ) && guardSelectedProjectFolderIsValid(context.selectedProjectFolderId),
        },
        setTranslationParameters: {actions: 'setTranslationParameters'},
        setProjectFolder: {actions: 'setProjectFolder'},
        setRelatedSchemasConfig: {actions: 'setRelatedSchemasConfig'},
        setSelectedDocumentId: {actions: 'setSelectedDocumentId'},
      },
    },
    saving: {
      invoke: {
        src: 'submitSpreadsheetImport',
        input: ({
          context,
        }: {
          context: AntibodyNestedDataCreationSpreadsheetImportState;
        }): NdcSpreadsheetImportRequest => prepareNdcSpreadsheetImportRequest(context),
        onDone: [
          {
            target: 'validationErrors',
            guard: ({event}) =>
              event.output?.validationErrors != null && event.output.validationErrors.length > 0,
            actions: assign(({context, event}) =>
              produce(context, (draft) => {
                draft.validationErrors = event.output!.validationErrors;
              })
            ),
          },
          {
            target: 'done',
            guard: ({event}) =>
              event.output?.validationErrors == null || event.output.validationErrors.length === 0,
          },
        ],
        onError: {
          target: 'finalize',
        },
      },
    },
    validationErrors: {
      on: {
        back: {
          target: 'finalize',
        },
        backToUploadSpreadsheet: {
          target: 'uploadSpreadsheet',
          actions: 'resetStateForSpreadsheetReupload',
        },
      },
    },
    done: {
      type: 'final',
    },
  },
});

export default AntibodyNestedDataCreationSpreadsheetImportModalMachine;
